import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Amplify } from 'aws-amplify';
import { getPatient } from '@/graphql/queries';
import { createReservation } from '@/graphql/mutations';
import awsconfig from '@/amplifyconfiguration.json';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, Close } from '@mui/icons-material';
import { fetchAuthSession } from 'aws-amplify/auth';
import { CircleOutlined, Remove } from '@mui/icons-material';

import { ReservationDateTimeList } from './ReservationDateTimeList';
import { ReservationFramesType } from '../types/ReservationFramesType';
import { ErrorDialog } from '@/features/misc/ErrorDialog';
import { useLoading } from '@/hooks/useLoading';
import { useFacilityPatients } from '@/hooks/useFacilityPatients';
import { ExaminationMenuType } from '../types/ExaminationMenuType';
import { useSearchParams } from 'react-router-dom';
import { dialogOpenEvent, RESERVATION_STEP, usePageViewEvent } from '../hooks/useGTMEvent';
import { useExaminationMenu } from '@/features/reservation/hooks/useOutPatient';
import { notMatchCustomErrorMessage } from '@/lib/sentryClient';
import * as Sentry from '@sentry/react';
import { executeGet, executePostAtAllResponse } from '@/client/rest';
import { CreateReservationMutation, GetPatientQuery } from '@/API';
import { executeQuery } from '@/client/graphql';
import { getMergedMenuList } from '@/utils/menuMerge';

// 電話番号は病院ごとにDBから動的に取得するのが望ましいが、
// 現在、電話番号はDBに格納されていないため、急ぎの対応としてハードコーディングしている
const REGE_PHONE_NUMBER = '0120-341-019';

Amplify.configure(awsconfig);

const formatDateToLocalISOString = (date: Date) => {
  const pad = (n: number) => (n < 10 ? '0' + n : n);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const getToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const now = getToday();
var reservationDateTime: Date | null = null;
var reservationDateTimeTo: Date | null = null;

export function ReservationDateTimeForm(props: any) {
  const {
    register,
    formState: { errors }
  } = useForm();
  const [reservationFrames, setReservationFrames] = useState<ReservationFramesType | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogDisabled, setConfirmDialogDisabled] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState<boolean>(false);
  const [backdropOpen, setBackdropOpen] = useState<boolean>(false);
  const [patient, setPatient] = useState<any>(null);
  const remarksRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [referenceDate, setReferenceDate] = useState<Date>(now);
  const [lastReservationDate, setLastReservationDate] = useState<Date>();
  const [fixReservationBodyData, setFixReservationBodyData] = useState<any>();
  const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState<string>('');
  const [unexpectedErrorDialogOpen, setUnexpectedErrorDialogOpen] = useState<boolean>(false);
  const [unexpectedErrorMessage, setUnexpectedErrorMessage] = useState<string>('');
  const [isSameReservationErrorDialogOpen, setIsSameReservationErrorDialogOpen] = useState<boolean>(false);
  const [calendarReset, setCalendarReset] = useState<boolean>(false);
  const facilityId = props.currentState.facilityId;
  const [membership, setMembership] = useState<any>({});
  const { examinationMenuList, vipMenu, exoMenu, subscriptionMenu, vipWithExoMenu, vipWithSubscriptionMenu } =
    useExaminationMenu(facilityId);
  const { getFacilityPatientsModel, postFacilityPatientsModel } = useFacilityPatients();
  const { loadingEvent, Loading } = useLoading();
  const [searchParams] = useSearchParams();
  const reservationDateTimeTimeunitFromQs = searchParams.get('reservation');
  var dow = referenceDate.getDay() > 0 ? referenceDate.getDay() - 1 : 6;
  var fromDate = new Date(referenceDate.getTime() - 1000 * 60 * 60 * 24 * dow);
  var toDate = new Date(fromDate.getTime() + 1000 * 60 * 60 * 24 * 6);
  var from =
    fromDate.getFullYear() + ('0' + (fromDate.getMonth() + 1)).slice(-2) + ('0' + fromDate.getDate()).slice(-2);
  var to = toDate.getFullYear() + ('0' + (toDate.getMonth() + 1)).slice(-2) + ('0' + toDate.getDate()).slice(-2);
  const location = useLocation();

  // サブスク会員
  const isSubscription = membership.subscription;

  // exo会員
  const isExo = membership.exo;

  // VIP会員
  const isVip = membership.vip;

  // 会員・オプションによって診療メニューを変更
  const mergedMenuList = getMergedMenuList(
    examinationMenuList,
    vipMenu,
    exoMenu,
    subscriptionMenu,
    vipWithExoMenu,
    vipWithSubscriptionMenu,
    isVip,
    isExo,
    isSubscription
  );

  useEffect(() => {
    if (patient === null) {
      (async () => {
        const session = await fetchAuthSession();
        if (session.userSub) {
          const result = await executeQuery<GetPatientQuery>(getPatient, { id: session.userSub as string }, 'userPool');
          setPatient(result.getPatient);

          // メンバーシップを取得
          // memberStatus = {vip: true, exo: false}のような形でmembershipにセットする
          const facilityPatient = await getFacilityPatientsModel(session.userSub ?? '', facilityId);

          let vipFlag = 0;
          let memberStatus = {};

          if (facilityPatient) {
            vipFlag = facilityPatient?.vip ? 1 : 0;
            if (facilityPatient?.memberStatus != null) {
              // exo会員の場合は、memberStatus = {exo:true}を取得
              memberStatus = JSON.parse(facilityPatient.memberStatus);
            }
          } else {
            // FacilityPatientテーブルのレコードが存在しない場合
            const mt = sessionStorage.getItem('mt');
            vipFlag = mt === '1' ? 1 : 0;
          }
          setMembership({ vip: vipFlag === 1 ? true : false, ...memberStatus! });
        }
      })();
    }
  }, []);

  // - 想定外のクエリパラメータを検出した場合、reservation クエリパラメータを削除する
  const removeQueryParamReservation = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('reservation');
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
    return;
  };

  function openDiaLog() {
    if (!props.isLogin) return;
    // 【非ログイン状態での予約処理】
    // 予約申請→ ログイン → 予約（この時点） のフロー
    // - 予約申請時に reservationDateTimeTimeunitFromQs がクエリパラメータに含まれる
    // - ただし、フォーマットエラーや予約選択の失敗時は不正データを除去する

    // - 非ログイン状態の予約フローに関係ない場合は処理をスキップ
    if (!reservationDateTimeTimeunitFromQs) return;

    const parts = reservationDateTimeTimeunitFromQs.split('-');
    // - フォーマットエラーの場合、クエリパラメータを削除して終了
    if (parts.length !== 3) {
      removeQueryParamReservation();
      return;
    }

    let [date, time, timeUnitString] = parts;
    try {
      onSelectFrame(date, time, Number(timeUnitString));
    } catch (e) {
      // - 予約選択に失敗した場合、クエリパラメータを削除
      removeQueryParamReservation();
      return;
    }
  }

  useEffect(() => {
    openDiaLog();
  }, []);

  /**
   * 診療メニュー名を取得しstateに保存 (LINEから遷移用)
   */
  useEffect(() => {
    const getOutPatientName = async () => {
      // EXO会員
      const response = await executeGet<{ outpatients: ExaminationMenuType[] }>({
        apiName: 'medirecoWebApi',
        path: '/new-get-outpatient-list',
        options: {
          queryParams: {
            facilityId: facilityId,
            vipFlag: membership.vip ? '1' : '0',
            exoFlag: membership.exo ? '1' : '0'
          }
        }
      });

      // サブスク会員
      const response2 = await executeGet<{ outpatients: ExaminationMenuType[] }>({
        apiName: 'medirecoWebApi',
        path: '/new-get-outpatient-list',
        options: {
          queryParams: {
            facilityId: facilityId,
            vipFlag: membership.vip ? '1' : '0',
            exoFlag: membership.subscription ? '2' : '0'
          }
        }
      });

      const outPatientMenus = [...response.outpatients, ...response2.outpatients];

      const outPatientName = outPatientMenus.find((menu) => {
        return menu.outpatientId === props.currentState.examinationMenuId;
      })?.outpatientName;

      if (outPatientName === undefined) return; // EXO会員の場合この分岐に入る

      props.setCurrentState((prevState: any) => ({
        ...prevState,
        examinationMenuName: outPatientName
      }));
    };

    getOutPatientName();
  }, [membership]);

  /**
   * 予約日時選択の候補一覧を取得
   */
  const fetchReservationData = async () => {
    const session = await fetchAuthSession();

    if (!facilityId) {
      // 施設IDがない場合は直接施設一覧画面にリダイレクト
      const redirectUrl = '/patient/reservation';
      const encodedRedirectUrl = encodeURIComponent(redirectUrl);
      navigate(`/patient/facilities?redirect=${encodedRedirectUrl}`, { replace: true });
      return;
    }
    const cognitoid = session.userSub as string;
    const outPatientId = props.currentState.examinationMenuId;
    const fromDate8 =
      String(fromDate.getFullYear()) +
      String(fromDate.getMonth() + 1).padStart(2, '0') +
      String(fromDate.getDate()).padStart(2, '0');

    const toDate8 =
      String(toDate.getFullYear()) +
      String(toDate.getMonth() + 1).padStart(2, '0') +
      String(toDate.getDate()).padStart(2, '0');

    setFixReservationBodyData({ cognitoid, outPatientId });

    const result = (await executeGet({
      apiName: 'medirecoWebApi',
      path: '/get-reservation-available-list',
      options: {
        queryParams: {
          facilityId: facilityId,
          cognitoId: 'cognitoid', // .NET側でCognitoIdを使用しなくなった(固定文字は必要)
          outpatientId: outPatientId,
          from: fromDate8,
          to: toDate8
        }
      }
    })) as any;

    const formatedLastDate = result.reservationLastDate;
    const lastYear = formatedLastDate.slice(0, 4);
    const lastMonth = formatedLastDate.slice(4, 6);
    const lastDate = formatedLastDate.slice(6, 8);
    const reservationLastDate = new Date(`${lastYear}/${lastMonth}/${lastDate}`);

    setLastReservationDate(reservationLastDate);
    setReservationFrames(result);
    setCalendarReset(false);
  };

  function addMonth(incremental: 1 | -1 | 0) {
    const oldReferenceDate: Date = referenceDate;
    const tmpReferenceDate = new Date(oldReferenceDate.getFullYear(), oldReferenceDate.getMonth() + incremental + 1, 0);
    if (tmpReferenceDate.getTime() < now.getTime()) {
      setReferenceDate(now);
    } else if (lastReservationDate && tmpReferenceDate.getTime() > lastReservationDate.getTime()) {
      setReferenceDate(lastReservationDate);
    } else {
      setReferenceDate(tmpReferenceDate);
    }
  }

  const addWeek = (incremental: 1 | -1 | 0) => {
    const tmpReferenceDate = new Date(referenceDate.getTime() + 1000 * 60 * 60 * 24 * 7 * incremental);
    if (tmpReferenceDate.getTime() < now.getTime()) {
      setReferenceDate(now);
    } else if (lastReservationDate && tmpReferenceDate.getTime() > lastReservationDate.getTime()) {
      setReferenceDate(lastReservationDate);
    } else {
      setReferenceDate(tmpReferenceDate);
    }
  };

  function onSelectFrame(date: string, time: string, timeUnit: number) {
    reservationDateTime = new Date(
      Number(date.slice(0, 4)),
      Number(date.slice(4, 6)) - 1,
      Number(date.slice(6, 8)),
      Number(time.slice(0, 2)),
      Number(time.slice(2, 4))
    );
    // 予約時間Toを算出
    reservationDateTimeTo = new Date(reservationDateTime.getTime());
    reservationDateTimeTo.setMinutes(reservationDateTimeTo.getMinutes() + timeUnit);

    props.setCurrentState({
      ...props.currentState,
      reservationDateTime: reservationDateTime,
      reservationDateTimeTo: reservationDateTimeTo
    });

    if (!props.isLogin) {
      // 非ログイン状態での予約処理
      // 予約申請（この時点) → ログイン → 予約 の流れになる
      // ログイン画面に遷移→予約確定画面に戻って来れるようにクエリパラメータでredirectUrlを設定
      const reservationFromQs = searchParams.get('reservation');
      if (reservationFromQs) return;

      // redirectUrlの作成
      const queryParams = new URLSearchParams(location.search);
      queryParams.set('reservation', date + '-' + time + '-' + timeUnit);
      // (例)redirectUrl=/patient/reservation?fid=0001&oid=010&reservation=20250206-1230-30 が入る
      const redirectUrl = `${location.pathname}?${queryParams}`;
      const encodedRedirectUrl = encodeURIComponent(redirectUrl);

      // LINEの場合、LINE移行登録orログインページに遷移
      const sourceFromQs = searchParams.get('source');
      if (sourceFromQs === 'line') {
        navigate(`/transferSignUp?redirectUrl=${encodedRedirectUrl}`);
        return;
      }

      navigate(`/?redirectUrl=${encodedRedirectUrl}`);
      return;
    }

    setConfirmDialogOpen(true);
    dialogOpenEvent({ dialogTitle: RESERVATION_STEP.ReserveConfirm, facilityId: props.currentState.facilityId });
  }

  async function fixReservation(remarksValue: string) {
    try {
      setBackdropOpen(true);
      setConfirmDialogDisabled(true);
      const session = await fetchAuthSession();

      let reservationDateTime = props.currentState.reservationDateTime;
      if (reservationDateTimeTimeunitFromQs) {
        // 【非ログイン状態での予約フロー対応】
        // 予約申請 → ログイン → 予約（この時点）の場合に
        // クエリパラメータから予約情報を取得
        const parts = reservationDateTimeTimeunitFromQs.split('-');
        if (parts.length !== 3) {
          throw new Error('予約日時のフォーマットが不正です' + reservationDateTimeTimeunitFromQs);
        }
        let [date, time, _] = parts;
        let reservationDateTimeFromQs = new Date(
          Number(date.slice(0, 4)),
          Number(date.slice(4, 6)) - 1,
          Number(date.slice(6, 8)),
          Number(time.slice(0, 2)),
          Number(time.slice(2, 4))
        );
        reservationDateTime = reservationDateTimeFromQs;
      } else {
        reservationDateTime = new Date(props.currentState.reservationDateTime.toISOString());
      }

      await executeQuery<CreateReservationMutation>(createReservation, {
        input: {
          patientId: session.userSub as string,
          symptoms: '', //props.currentState.symptoms.join('|'),
          symptomOthers: props.currentState.symptomOthers ?? '',
          examinationMenuId: props.currentState.examinationMenuId,
          remarks: remarksValue,
          dateTime: reservationDateTime
        }
      });

      const date = reservationDateTime
        ? reservationDateTime?.getFullYear().toString().padStart(4, '0') +
          (reservationDateTime?.getMonth() + 1).toString().padStart(2, '0') +
          reservationDateTime?.getDate().toString().padStart(2, '0')
        : '';

      const startTime = reservationDateTime
        ? reservationDateTime?.getHours().toString().padStart(2, '0') +
          reservationDateTime.getMinutes().toString().padStart(2, '0')
        : '';
      const endTime = reservationDateTimeTo
        ? reservationDateTimeTo?.getHours().toString().padStart(2, '0') +
          reservationDateTimeTo.getMinutes().toString().padStart(2, '0')
        : '';

      if (!facilityId) {
        // 施設IDがない場合は直接施設一覧画面にリダイレクト
        const redirectUrl = '/patient/reservation';
        const encodedRedirectUrl = encodeURIComponent(redirectUrl);
        navigate(`/patient/facilities?redirect=${encodedRedirectUrl}`, { replace: true });
        return;
      }

      if ((await getFacilityPatientsModel(fixReservationBodyData.cognitoid, facilityId)) == null) {
        await postFacilityPatientsModel({
          patientId: fixReservationBodyData.cognitoid,
          facilityId: facilityId,
          vip: false,
          memberStatus: '',
          isFirstVisit: true
        });
      }

      const response = await executePostAtAllResponse({
        apiName: 'medirecoWebApi',
        path: '/fix-reservation',
        options: {
          body: {
            facilityId: facilityId,
            cognitoId: fixReservationBodyData.cognitoid,
            outpatientId: fixReservationBodyData.outPatientId,
            outpatientName: props.currentState.examinationMenuName,
            reservationDate: date,
            startTime: startTime,
            endTime: endTime,
            memo: remarksValue
          }
        }
      });

      if (response.statusCode === 200) {
        props.setActiveStep(4); // 予約完了を表示する
      } else {
        switch (response.statusCode) {
          case 400:
            setCompleteDialogOpen(false);
            setErrorDialogMessage(await response.body.text());
            setErrorDialogOpen(true);
            break;
          default:
            setUnexpectedErrorMessage(
              '予期せぬエラーが発生しました。\n診療メニュー選択からやり直してください。\nコード：' +
                response.statusCode.toString()
            );
            setUnexpectedErrorDialogOpen(true);
            break;
        }
      }
      setConfirmDialogDisabled(false);
      setConfirmDialogOpen(false);
    } catch (e) {
      if (e instanceof Error && notMatchCustomErrorMessage(e.name)) {
        Sentry.captureException(e);
      }

      console.error(e);
      // RDSに病院の電話番号を入れるのが望ましいが、急ぎの対応のためリジェの電話番号をハードコーディングしている
      let phoneNumber = '';
      if (facilityId === '0001') {
        phoneNumber = REGE_PHONE_NUMBER;
      }
      setUnexpectedErrorMessage(
        `予期せぬエラーが発生しました。\n診療メニュー選択からやり直してください。\n\nまた、メディレコに登録すると、携帯電話やパソコンから簡単に予約確認できるようになります（無料）。\nぜひメディレコからの予約をご検討ください。\n\nなお、こちらのクリニック(${props.currentState.facilityName})へのお電話での予約も可能です。\nお電話でのご予約をご希望の場合は、tel:${phoneNumber}までご連絡ください`
      );
      setUnexpectedErrorDialogOpen(true);
      setConfirmDialogDisabled(false);
    } finally {
      setBackdropOpen(false);
    }
  }

  //予約日程変更処理の追加
  async function changeReservation(remarksValue: string) {
    try {
      setBackdropOpen(true);
      setConfirmDialogDisabled(true);
      const session = await fetchAuthSession();

      await executeQuery<CreateReservationMutation>(createReservation, {
        input: {
          patientId: session.userSub as string,
          symptoms: '', //props.currentState.symptoms.join('|'),
          symptomOthers: props.currentState.symptomOthers ?? '',
          examinationMenuId: props.currentState.examinationMenuId,
          remarks: remarksValue,
          dateTime: props.currentState.reservationDateTime.toISOString()
        }
      });

      const date = reservationDateTime
        ? reservationDateTime?.getFullYear().toString().padStart(4, '0') +
          (reservationDateTime?.getMonth() + 1).toString().padStart(2, '0') +
          reservationDateTime?.getDate().toString().padStart(2, '0')
        : '';

      const startTime = reservationDateTime
        ? reservationDateTime?.getHours().toString().padStart(2, '0') +
          reservationDateTime.getMinutes().toString().padStart(2, '0')
        : '';
      const endTime = reservationDateTimeTo
        ? reservationDateTimeTo?.getHours().toString().padStart(2, '0') +
          reservationDateTimeTo.getMinutes().toString().padStart(2, '0')
        : '';

      const res = await executePostAtAllResponse({
        apiName: 'medirecoWebApi',
        path: '/change-reservation',
        options: {
          body: {
            cognitoId: fixReservationBodyData.cognitoid,
            reserveId: props.currentState.reserveid,
            BeforeChangeOutpatientId: props.currentState.examinationMenuId,
            BeforeChangeReservateionDate: props.currentState.reservationDate,
            BeforeChangeStartTime: props.currentState.reservationStartime,
            outpatientId: fixReservationBodyData.outPatientId,
            reservationDate: date,
            startTime: startTime,
            endTime: endTime,
            memo: remarksValue
          }
        }
      });
      if (res.statusCode === 200) {
        props.setActiveStep(5); // 予約完了を表示する
      } else {
        switch (res.statusCode) {
          case 400:
            props.toErrorPage(await res.body.text(), 'ご予約日時変更エラー');
            break;
          default:
            // setSomethingErrorDialogOpen(true);
            break;
        }
      }
      setConfirmDialogDisabled(false);
      setConfirmDialogOpen(false);

      props.setActiveStep(5);
    } catch (e) {
      if (e instanceof Error && notMatchCustomErrorMessage(e.name)) {
        Sentry.captureException(e);
      }

      // setSomethingErrorDialogOpen(true);
      setConfirmDialogDisabled(false);
    } finally {
      setBackdropOpen(false);
    }
  }

  const handleSaveReservation = async () => {
    // 既存の予約と重複していれば、エラーを出して予約させないようにする
    const isMenuReservable = mergedMenuList.find((menu) => menu.outpatientId === props.currentState.examinationMenuId);
    if (!isMenuReservable) {
      // 既に同じメニューを予約している場合は, この分岐に入る
      // エラーを出して診療メニューを選ばせる

      // RDSに病院の電話番号を入れるのが望ましいが、急ぎの対応のためリジェの電話番号をハードコーディングしている
      let phoneNumber = '';
      if (facilityId === '0001') {
        phoneNumber = REGE_PHONE_NUMBER;
      }
      setUnexpectedErrorMessage(
        `選択された時間枠は、予約及び会員登録の手続き中に他のお客様が同じ時間帯にて予約を確定されたため、ご利用いただけなくなりました。\n診療メニュー選択からやり直してください。\n\nまた、メディレコに登録すると、携帯電話やパソコンから簡単に予約確認できるようになります（無料）。\nぜひメディレコからの予約をご検討ください。\n\nなお、こちらのクリニック(${props.currentState.facilityName})へのお電話での予約も可能です。\nお電話でのご予約をご希望の場合は、tel:${phoneNumber}までご連絡ください`
      );
      setIsSameReservationErrorDialogOpen(true);
      return;
    }

    const remarksValue = remarksRef.current?.value || '';

    try {
      const session = await fetchAuthSession();
      const result = await executeQuery<GetPatientQuery>(getPatient, { id: session.userSub as string }, 'userPool');

      const patient = result?.getPatient;

      if (patient == null) {
        setConfirmDialogOpen(false);
        return props.handleNext();
      } else {
        if (props.currentState.isChange) {
          changeReservation(remarksValue);
        } else {
          fixReservation(remarksValue);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  async function handleCloseDialog() {
    setConfirmDialogOpen(false);
  }

  function getDayOfWeek(date: Date) {
    const week = ['日', '月', '火', '水', '木', '金', '土'];
    return week[date.getDay()];
  }

  function isValidDate(d: Date) {
    return d instanceof Date && !isNaN(d.getTime());
  }

  const handleCalendarClear = () => {
    setReferenceDate(now);
    setCalendarReset(true);
  };

  useEffect(() => {
    console.log(referenceDate);
    loadingEvent(async () => {
      // 予約可能日時の設定
      await fetchReservationData();
    });
  }, [referenceDate]);

  usePageViewEvent({ pageTitle: RESERVATION_STEP.Date, facilityId });

  return (
    <Loading>
      <Box sx={{ p: 2 }}>
        <Grid container direction={'row'} spacing={2}>
          <Grid item xs={0} md={3} sm={2} />
          <Grid item xs={12} md={6} sm={8}>
            <form>
              <Grid container direction={'column'} spacing={2}>
                <Grid item sx={{ textAlign: 'center' }} mt={1} mb={1}>
                  <Typography sx={{ fontWeight: '700' }}>
                    ご選択メニュー：{props.currentState.examinationMenuName}
                  </Typography>
                </Grid>
                <Grid item sx={{ textAlign: 'left' }}>
                  <Grid container direction={'row'} spacing={2} justifyContent="center">
                    <Grid item>
                      <Box sx={{ '&:hover': { cursor: 'pointer' }, maxWidth: '160px' }}>
                        <TextField
                          type="date"
                          label="日付選択*"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={
                            isValidDate(referenceDate)
                              ? formatDateToLocalISOString(referenceDate)
                              : formatDateToLocalISOString(now)
                          }
                          {...register('referenceDate', {
                            onChange: (e) => {
                              const newDate = new Date(e.target.value);
                              setReferenceDate(isValidDate(newDate) ? newDate : now);
                            }
                          })}
                          error={!!errors.referenceDate}
                          inputProps={{
                            style: {
                              cursor: 'pointer'
                            }
                          }}
                        />
                      </Box>
                      <Typography color="error" style={{ fontSize: '12px', marginLeft: '8px' }}>
                        {errors.referenceDate?.message?.toString()}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container direction={'row'} spacing={0.5} justifyContent="right" alignItems="center" mt={3}>
                    <Grid item className="text-primary">
                      <CircleOutlined />
                    </Grid>
                    <Grid item>
                      <Typography>受付可能</Typography>
                    </Grid>
                    <Grid item className="text-primary">
                      <Remove />
                    </Grid>
                    <Grid item>
                      <Typography>受付不可</Typography>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    mt={0.3}
                    direction={'row'}
                    spacing={2}
                    justifyContent="space-between"
                    sx={{ height: '40px' }}
                  >
                    <Grid item>
                      <Grid container direction={'row'} sx={{ position: 'relative' }}>
                        <Grid item sx={{ position: 'absolute', left: '0' }}>
                          {now.getTime() <
                            new Date(
                              referenceDate.getFullYear(),
                              referenceDate.getMonth() - 1,
                              referenceDate.getDate()
                            ).getTime() && (
                            <HandleDateButton label="前月" onClick={() => addMonth(-1)} showDoubleArrowLeft />
                          )}
                        </Grid>
                        <Grid item sx={{ position: 'absolute', left: '60px' }}>
                          {now.getTime() < fromDate.getTime() && (
                            <HandleDateButton label="前週" onClick={() => addWeek(-1)} showArrowLeft />
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item>
                      <Grid container direction={'row'} sx={{ position: 'relative' }}>
                        <Grid item sx={{ position: 'absolute', right: '60px' }}>
                          {lastReservationDate && toDate.getTime() < lastReservationDate.getTime() && (
                            <HandleDateButton label="翌週" onClick={() => addWeek(1)} showArrowRight />
                          )}
                        </Grid>
                        <Grid item sx={{ position: 'absolute', right: '0' }}>
                          {lastReservationDate &&
                            lastReservationDate.getTime() >
                              new Date(
                                referenceDate.getFullYear(),
                                referenceDate.getMonth() + 1,
                                referenceDate.getDate()
                              ).getTime() && (
                              <HandleDateButton label="翌月" onClick={() => addMonth(1)} showDoubleArrowRight />
                            )}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item sx={{ height: '65vh' }}>
                  <ReservationDateTimeList
                    from={from}
                    to={to}
                    reservationFrames={reservationFrames}
                    onSelectFrame={onSelectFrame}
                    onCalendarClear={handleCalendarClear}
                    calendarReset={calendarReset}
                  />
                </Grid>
                {props.isLogin && (
                  // 非ログイン状態の場合は診療メニューが表示されない仕様
                  <Grid item mt={2}>
                    <ActionButton
                      label="診療メニュー選択に戻る"
                      onClick={() => {
                        // 予約日時変更の場合
                        if (props.currentState.isChange) {
                          navigate(`/patient/reservation?fid=${facilityId}`);
                          return;
                        }

                        // 新規予約日時変更の場合
                        props.setActiveStep(1);
                      }}
                      showBackIcon
                    />
                  </Grid>
                )}
              </Grid>
            </form>
          </Grid>
          <Grid item xs={0} md={3} sm={2} />
        </Grid>
        <Dialog
          open={confirmDialogOpen}
          disableEscapeKeyDown={confirmDialogDisabled}
          onClose={(event, reason) => {
            if (reason && reason === 'backdropClick') return;
          }}
          aria-labelledby="dialog-title"
        >
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8
            }}
          >
            <Close />
          </IconButton>
          <DialogTitle
            id="dialog-title"
            data-gtm-dialog-title={'受付内容'}
            sx={{ textAlign: 'center', fontWeight: '700' }}
          >
            {'受付内容'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="subtitle2" marginLeft={'8px'} marginTop={'8px'} fontWeight={'bold'}>
              診療メニュー
            </Typography>
            <Typography variant="body2" marginLeft={'8px'}>
              {props.currentState.examinationMenuName}
            </Typography>
            {/* 予約時のフロー変更に伴い一旦非表示 */}
            {/* <Typography variant="subtitle2" marginLeft={'8px'} marginTop={'8px'} fontWeight={'bold'}>
            お名前
          </Typography> */}
            {/* <Typography variant="body2" marginLeft={'8px'}>
            {patient ? patient.familyName + patient.firstName : ''} 様
          </Typography> */}
            <Typography variant="subtitle2" marginLeft={'8px'} marginTop={'8px'} fontWeight={'bold'}>
              ご予約日時
            </Typography>
            <Typography variant="body2" marginLeft={'8px'}>
              {reservationDateTime ? reservationDateTime.getFullYear() : 'XXXX'}年
              {reservationDateTime ? reservationDateTime.getMonth() + 1 : 'XX'}月
              {reservationDateTime ? reservationDateTime.getDate() : 'XX'}日(
              {reservationDateTime ? getDayOfWeek(reservationDateTime) : 'X'})
              {reservationDateTime ? reservationDateTime.getHours() : 'XX'}:
              {reservationDateTime ? reservationDateTime.getMinutes().toString().padStart(2, '0') : 'XX'}
            </Typography>
            <Typography variant="subtitle2" marginLeft={'8px'} marginTop={'8px'} fontWeight={'bold'}>
              受付メモ
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <Box sx={{ maxWidth: '90%', width: '100%' }}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  defaultValue={props.currentState.remarks || ''}
                  inputRef={remarksRef}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <Paper
                style={{
                  overflowY: 'scroll',
                  maxHeight: '300px',
                  padding: '10px',
                  marginTop: '20px',
                  marginBottom: '20px',
                  border: '1px solid lightgray',
                  borderRadius: '4px',
                  maxWidth: '90%',
                  width: '100%'
                }}
              >
                <Typography className="text-primary text-center font-bold text-sm" mb={2}>
                  ご注意事項
                </Typography>
                <Typography>
                  当院の診療は全て予約制です。
                  <br />
                  予約時間からの診療を保証するものではなく、来院時間の目安となります。
                  <br />
                  診療開始が遅れる場合には、患者様にご連絡いたしますので、指示に従って受診ください。
                  <br />
                  また、当日の予約時間の5分前までにオンライン事前問診されていない場合や、受付時間までに自動受付をしないと予約は自動キャンセルとなります。
                  <br />
                  予約が自動的にキャンセルされた場合、次回以降の予約可能時間に制限がかかりますのでご注意ください。
                </Typography>
              </Paper>
            </Box>
          </DialogContent>

          <DialogActions sx={{ mb: 5 }}>
            <ActionButton
              label="上記の内容で確定する"
              onClick={() => {
                const remarksValue = remarksRef.current?.value || '';

                // 受付メモをstateに保存
                props.setCurrentState({ ...props.currentState, remarks: remarksValue });

                if (props.currentState.isChange) {
                  changeReservation(remarksValue);
                } else {
                  handleSaveReservation();
                }
              }}
              showNextIcon
            />
          </DialogActions>
        </Dialog>
        <Dialog
          open={completeDialogOpen}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{'予約完了'}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">予約が完了しました。</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setCompleteDialogOpen(false);
                navigate('/patient/medical-consultation', {
                  state: { reservationId: props.currentState.reservationId }
                });
              }}
              autoFocus
            >
              問診に進む
            </Button>
          </DialogActions>
        </Dialog>

        <ErrorDialog
          open={unexpectedErrorDialogOpen}
          title="予約の確定に失敗しました"
          buttonLabel="診療メニュー選択に戻る"
          buttonAction={() => {
            // 予約日時変更の場合
            if (props.currentState.isChange) {
              navigate(`/patient/reservation?fid=${facilityId}`);
              return;
            }

            // 新規予約日時変更の場合
            props.setActiveStep(1);
          }}
        >
          {unexpectedErrorMessage}
        </ErrorDialog>

        <ErrorDialog
          open={isSameReservationErrorDialogOpen}
          title="予約の確定に失敗しました"
          buttonLabel="診療メニュー選択に戻る"
          buttonAction={() => {
            if (props.currentState.isChange) {
              navigate(`/patient/reservation?fid=${facilityId}`);
              return;
            }

            // 新規予約日時変更の場合
            props.setActiveStep(1);
          }}
          contactSupportMessage="このエラーが何度も表示される場合は、お手数をおかけしますがお問い合わせください"
        >
          {unexpectedErrorMessage}
        </ErrorDialog>

        <ErrorDialog
          open={errorDialogOpen}
          title="予約を確定できませんでした"
          buttonLabel="閉じる"
          buttonAction={() => {
            setErrorDialogOpen(false);
            fetchReservationData();
          }}
        >
          {errorDialogMessage}
        </ErrorDialog>

        <Backdrop
          sx={{ color: '#fff', backgroundColor: '#0003', zIndex: (theme) => theme.zIndex.modal + 1 }}
          open={backdropOpen}
        >
          <CircularProgress color={'inherit'} />
        </Backdrop>
      </Box>
    </Loading>
  );
}

// MEMO: 条件が変わるたびに都度画像を読み込むと画像だけ表示が遅くなるので先に定義している。
const DoubleArrowLeft = React.memo(() => {
  return <img src="/images/double-arrow-left.svg" alt="doubleArrowLeft" className="text-sm mr-1" />;
});

const ArrowLeft = React.memo(() => {
  return <img src="/images/arrow-left.svg" alt="arrowLeft" className="text-sm mr-1" />;
});

const ArrowRight = React.memo(() => {
  return <img src="/images/arrow-right.svg" alt="arrowRight" className="text-sm ml-1" />;
});

const DoubleArrowRight = React.memo(() => {
  return <img src="/images/double-arrow-right.svg" alt="doubleArrowRight" className="text-sm ml-1" />;
});

interface HandleDateButtonProps {
  label: string;
  onClick: () => void;
  showArrowLeft?: boolean;
  showDoubleArrowLeft?: boolean;
  showArrowRight?: boolean;
  showDoubleArrowRight?: boolean;
}

const HandleDateButton = ({
  label,
  onClick,
  showArrowLeft,
  showDoubleArrowLeft,
  showArrowRight,
  showDoubleArrowRight
}: HandleDateButtonProps) => {
  return (
    <div
      onClick={onClick}
      style={{ width: '55px', height: '32px' }}
      className="rounded bg-transparent text-primary border-solid border border-primary flex items-center justify-center cursor-pointer"
    >
      <div className="flex items-center">
        {showDoubleArrowLeft ? (
          <div>
            <DoubleArrowLeft />
          </div>
        ) : (
          <></>
        )}

        {showArrowLeft ? (
          <div>
            <ArrowLeft />
          </div>
        ) : (
          <></>
        )}

        <div className="text-sm font-bold">{label}</div>

        {showArrowRight ? (
          <div>
            <ArrowRight />
          </div>
        ) : (
          <></>
        )}

        {showDoubleArrowRight ? (
          <div>
            <DoubleArrowRight />
          </div>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

interface ActionButtonProps {
  label: string;
  onClick: () => void;
  showBackIcon?: boolean;
  showNextIcon?: boolean;
}
const ActionButton = ({ label, onClick, showBackIcon, showNextIcon }: ActionButtonProps) => {
  return (
    <button
      onClick={onClick}
      style={{ width: '200px', height: '36px', color: '#FFFFFF', textAlign: 'center', margin: 'auto' }}
      className="bg-primary flex relative rounded"
    >
      {showBackIcon ? (
        <div>
          <ArrowBackIos style={{ position: 'absolute', top: '11px', left: '9px', fontSize: '14px' }} />
        </div>
      ) : (
        <></>
      )}

      <div className="text-sm m-auto font-bold">{label}</div>

      {showNextIcon ? (
        <div>
          <ArrowForwardIos style={{ position: 'absolute', top: '11px', right: '11px', fontSize: '14px' }} />
        </div>
      ) : (
        <></>
      )}
    </button>
  );
};
